<script setup>
import { ref, computed, watch } from 'vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import TranslationOptionsModal from './TranslationOptionsModal.vue'
import { FALLBACK_CATALOG, languageName, joinLanguageNames } from './captionLanguages'
import { GEMINI_HELP, optionsFor, optionsSummary, translatedLangs } from './captionTranslations'

/**
 * "Translate with Gemini" for a flow's translated tracks, with one summary row
 * per language on Gemini; Edit opens the per-language options in a modal.
 * Edits a draft `{ gemini, options: { [lang]: { formality, compress,
 * genderAware, instructions } } }`; `buildTranslations` turns it into the map
 * core receives.
 */
const draft = defineModel({ type: Object, required: true })

const props = defineProps({
	source: { type: String, default: '' },
	langs: { type: Array, default: () => [] },
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
	// Recognition "Speaker labels" is on: gender-aware translation needs it.
	speakers: { type: Boolean, default: false },
	// Translated languages a non-English pair puts on Gemini even with the switch off.
	required: { type: Array, default: () => [] },
	// { [lang]: { [field]: message } } from validation and the last save.
	errors: { type: Object, default: () => ({}) },
	// Mapped errors from the last 400 `captions_invalid_translations`; a new
	// set opens the modal on the first language that has one.
	serverErrors: { type: Object, default: () => ({}) },
	disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle'])

const translated = computed(() => translatedLangs(props.source, props.langs))
// The languages on Gemini, whose options can be adjusted.
const targets = computed(() => (draft.value.gemini ? translated.value : translated.value.filter((l) => props.required.includes(l))))
const autoOnly = computed(() => !draft.value.gemini && targets.value.length > 0)
const name = (code) => languageName(code, props.catalog)
const errorCount = (lang) => Object.keys(props.errors?.[lang] || {}).length

const gemini = computed({
	get: () => !!draft.value.gemini,
	set: (on) => {
		draft.value = { ...draft.value, gemini: on }
		emit('toggle', on)
	},
})

const modalOpen = ref(false)
const modalLang = ref('')

function edit(lang) {
	modalLang.value = lang
	modalOpen.value = true
}

function apply(options) {
	draft.value = { ...draft.value, options }
}

watch(() => props.serverErrors, (errs) => {
	const first = targets.value.find((lang) => Object.keys(errs || {}).some((k) => k.toLowerCase() === lang.toLowerCase()))
	if (first) edit(first)
})
</script>

<template>
	<div class="flex flex-col gap-2" role="group" aria-labelledby="caption-translation-label" data-testid="translation-engine">
		<div class="flex items-center justify-between gap-2">
			<span id="caption-translation-label" class="text-tiny font-medium text-surface-text-1">Translation</span>
			<FormSwitch
				id="caption-gemini"
				v-model="gemini"
				size="sm"
				label="Translate with Gemini"
				label-position="left"
				label-styles="text-xs text-surface-text-1"
				:disabled="disabled"
			/>
		</div>

		<ul
			v-if="targets.length"
			class="m-0 flex list-none flex-col divide-y divide-white/5 overflow-hidden rounded bg-surface-3 p-0 ring-1 ring-white/5"
			data-testid="translation-rows"
		>
			<li
				v-for="lang in targets"
				:key="lang"
				class="flex min-h-9 items-center justify-between gap-2 py-1 pl-3 pr-1"
				:data-translation-lang="lang"
				data-testid="translation-row"
			>
				<span class="min-w-0 truncate text-xs">
					<span class="text-tiny font-medium text-surface-text-2">{{ name(lang) }}</span>
					<span class="text-surface-7"> · <span data-testid="translation-row-summary">{{ optionsSummary(optionsFor(draft, lang), speakers) }}</span></span>
					<span v-if="!gemini" class="text-surface-7"> · Gemini · required</span>
					<span v-if="errorCount(lang)" class="text-error-light" data-testid="translation-row-errors"> · {{ errorCount(lang) }} to fix</span>
				</span>
				<FormButton
					type="link"
					size="xs"
					class="!px-2 text-xs hover:bg-surface-4"
					:aria-label="`Edit ${name(lang)} translation options`"
					@click="edit(lang)"
				>
					Edit
				</FormButton>
			</li>
		</ul>

		<p class="m-0 text-xs text-surface-7" data-testid="translation-engine-help">
			<template v-if="autoOnly">{{ joinLanguageNames(targets, catalog) }} already {{ targets.length === 1 ? 'uses' : 'use' }} Gemini (required for {{ targets.length === 1 ? 'this pair' : 'these pairs' }}).</template>
			<template v-else>{{ GEMINI_HELP }}</template>
		</p>

		<TranslationOptionsModal
			v-model="modalOpen"
			:draft="draft"
			:targets="targets"
			:initial-lang="modalLang"
			:source="source"
			:langs="langs"
			:catalog="catalog"
			:speakers="speakers"
			:server-errors="serverErrors"
			:disabled="disabled"
			@apply="apply"
		/>
	</div>
</template>
